import '../auth.scss';
import './register-v2.scss';
import 'react-phone-number-input/style.css';

import { ReactComponent as SuccessIcon } from '@basqet-dashboard/common/assets/icons/success.svg';
import { ReactComponent as Logo } from '@basqet-dashboard/common/assets/logo.svg';
import {
  Button,
  FormInput,
  Modal,
  OTPInput,
  PasswordStrengthMeter,
  PhoneNumberInput
} from '@basqet-dashboard/common/components';
import { APP_NAME } from '@basqet-dashboard/common/config';
import { UserContext } from '@basqet-dashboard/common/context';
import { useDocumentTitle, useSessionStorage } from '@basqet-dashboard/common/hooks';
import {
  useResendOTPMutation,
  useSignupV2Mutation,
  useVerifyOTPMutation
} from '@basqet-dashboard/common/hooks/mutations/auth';
import { formatErrorMessage, signupV2Schema } from '@basqet-dashboard/common/utils';
import { AccountTypeSelector } from '@components';
import { ANALYTICS_EVENTS } from '@config';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAnalytics, useTrackOnMount } from '@hooks';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { ACCOUNT_TYPE_TO_API } from '../../../config/account-types';

const NO_PENDING_REGISTRATION = 'No pending registration';

function handleExpiredSession(error, onRestart) {
  const message = error.response?.data?.message;
  if (message?.includes(NO_PENDING_REGISTRATION)) {
    toast.error(message);
    onRestart();
    return true;
  }
  return false;
}

const VIEWS = { FORM: 'form', OTP: 'otp' };

function RegisterV2() {
  const location = useLocation();
  const verifyEmail = new URLSearchParams(location.search).get('verify_email');

  const [currentView, setCurrentView] = useState(verifyEmail ? VIEWS.OTP : VIEWS.FORM);
  const [userEmail, setUserEmail] = useState(verifyEmail || '');
  const [maskedEmail, setMaskedEmail] = useState('');

  useDocumentTitle('Create an Account');

  const handleFormSuccess = (rawEmail, serverMaskedEmail) => {
    setUserEmail(rawEmail);
    setMaskedEmail(serverMaskedEmail);
    setCurrentView(VIEWS.OTP);
  };

  const handleRestart = () => {
    setCurrentView(VIEWS.FORM);
    setUserEmail('');
    setMaskedEmail('');
  };

  return (
    <main className="auth register-v2">
      {currentView === VIEWS.FORM && <SignupFormV2 onSuccess={handleFormSuccess} />}
      {currentView === VIEWS.OTP && (
        <OTPVerification
          email={userEmail}
          maskedEmail={maskedEmail}
          onMaskedEmailChange={setMaskedEmail}
          onRestart={handleRestart}
          autoSend={!!verifyEmail}
        />
      )}
    </main>
  );
}

function SignupFormV2({ onSuccess }) {
  const { track } = useAnalytics();
  const { mutateAsync: signupV2, isLoading } = useSignupV2Mutation();
  const hasTrackedStart = useRef(false);

  useTrackOnMount(ANALYTICS_EVENTS.SIGNUP_PAGE_VIEWED);

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(signupV2Schema)
  });

  const passwordValue = watch('password', '');

  const handleFormFocus = () => {
    if (!hasTrackedStart.current) {
      track(ANALYTICS_EVENTS.SIGNUP_FORM_STARTED);
      hasTrackedStart.current = true;
    }
  };

  const onSubmit = async (body) => {
    track(ANALYTICS_EVENTS.SIGNUP_SUBMITTED);
    // eslint-disable-next-line no-unused-vars
    const { terms_accepted, account_type, ...rest } = body;
    const payload = { ...rest, account_type: ACCOUNT_TYPE_TO_API[account_type] };
    try {
      const response = await signupV2(payload);
      onSuccess(body.email, response.data.email);
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error('An account with this email already exists');
      } else {
        formatErrorMessage(error);
      }
    }
  };

  return (
    <div className="auth__content">
      <h1 className="page__title">
        Create a {APP_NAME} <span className="text-cursive">account</span>
      </h1>
      <div className="auth__card">
        <Logo className="logo" />
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="register-v2__form"
          onFocus={handleFormFocus}>
          <div className="form__grid">
            <FormInput
              label="First name"
              placeholder="Enter your first name"
              name="first_name"
              type="text"
              error={errors.first_name?.message}
              register={register('first_name')}
            />
            <FormInput
              label="Last name"
              placeholder="Enter your last name"
              name="last_name"
              type="text"
              error={errors.last_name?.message}
              register={register('last_name')}
            />
          </div>

          <FormInput
            label="Email"
            placeholder="Enter your email"
            name="email"
            type="email"
            error={errors.email?.message}
            register={register('email')}
          />

          <div className="register-v2__password-field">
            <FormInput
              label="Password"
              placeholder="Enter your password"
              name="password"
              type="password"
              error={errors.password?.message}
              {...register('password')}
            />
            <PasswordStrengthMeter password={passwordValue} />
          </div>

          <PhoneNumberInput
            label="Phone Number"
            control={control}
            error={errors.phone_number?.message}
          />

          <div className="register-v2__account-type">
            <span className="form__label" id="account-type-label">
              Account Type
            </span>
            <Controller
              name="account_type"
              control={control}
              render={({ field }) => (
                <AccountTypeSelector
                  name="account_type"
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.account_type?.message}
                />
              )}
            />
          </div>

          <div className="register-v2__terms">
            <label className="register-v2__terms-label" htmlFor="terms_accepted">
              <input type="checkbox" id="terms_accepted" {...register('terms_accepted')} />
              <span>
                By checking this box, I agree to the{' '}
                <a href="https://basqet.com/terms-of-use" target="_blank" rel="noreferrer">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="https://basqet.com/privacy-policy" target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
              </span>
            </label>
            {errors.terms_accepted && (
              <p className="register-v2__terms-error">{errors.terms_accepted.message}</p>
            )}
          </div>

          <Button isFull isLoading={isLoading} disabled={!isValid}>
            Sign up
          </Button>
        </form>
        <p className="auth__link">
          I already have an account, <Link to="/login">Log in</Link>
        </p>
      </div>
    </div>
  );
}

SignupFormV2.propTypes = {
  onSuccess: PropTypes.func.isRequired
};

function OTPVerification({ email, maskedEmail, onMaskedEmailChange, onRestart, autoSend }) {
  const { track } = useAnalytics();
  const { setUser } = useContext(UserContext);
  const [, setToken] = useSessionStorage(APP_NAME, '');
  const { mutateAsync: verifyOTPAction, isLoading: isVerifying } = useVerifyOTPMutation();
  const { mutateAsync: resendOTPAction, isLoading: isResending } = useResendOTPMutation();

  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(60);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const otpInputRef = useRef(null);
  const hasTrackedCodeEntered = useRef(false);
  const hasSentInitialOTP = useRef(false);

  useTrackOnMount(ANALYTICS_EVENTS.EMAIL_VERIFICATION_VIEWED);

  // Auto-send OTP when redirected from login with unverified email
  useEffect(() => {
    if (!autoSend || hasSentInitialOTP.current) return;
    hasSentInitialOTP.current = true;
    let cancelled = false;

    resendOTPAction({ email })
      .then((response) => {
        if (cancelled) return;
        onMaskedEmailChange(response.data.email);
        toast.success('Verification code sent to your email');
      })
      .catch(() => {
        if (cancelled) return;
        toast.error('Failed to send verification code. Please try again.');
      });

    return () => {
      cancelled = true;
    };
  }, []); // intentionally empty — fire once on mount only

  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCountdown]);

  const handleOtpChange = useCallback(
    (value) => {
      setOtpValue(value);
      if (value.length === 6 && !hasTrackedCodeEntered.current) {
        track(ANALYTICS_EVENTS.EMAIL_CODE_ENTERED);
        hasTrackedCodeEntered.current = true;
      }
      if (otpError) {
        setOtpError('');
      }
    },
    [otpError, track]
  );

  const handleVerify = async () => {
    track(ANALYTICS_EVENTS.VERIFY_EMAIL_CLICKED);
    if (otpValue.length !== 6) {
      setOtpError('Please enter a 6-digit code');
      return;
    }

    try {
      const response = await verifyOTPAction({ email, otp: otpValue });
      const { accessToken, merchantUser } = response.data;
      setToken({ token: accessToken, merchantUser });
      setUser(merchantUser);
      setShowSuccessModal(true);
    } catch (error) {
      if (handleExpiredSession(error, onRestart)) return;
      const message =
        error.response?.data?.message || 'Invalid verification code. Please try again.';
      setOtpError(message);
    }
  };

  const handleResend = async () => {
    if (resendCountdown > 0) return;

    try {
      await resendOTPAction({ email });
      track(ANALYTICS_EVENTS.EMAIL_CODE_RESENT);
      toast.success('Verification code resent!');
      setResendCountdown(60);
      hasTrackedCodeEntered.current = false;
      setOtpValue('');
      setOtpError('');
    } catch (error) {
      if (handleExpiredSession(error, onRestart)) return;
      if (error.response?.status === 429) {
        const retryAfter = error.response.data?.retryAfter || 60;
        setResendCountdown(retryAfter);
        toast.error(`Too many requests. Please try again in ${retryAfter}s.`);
      } else {
        formatErrorMessage(error);
      }
    }
  };

  useEffect(() => {
    if (showSuccessModal) {
      track(ANALYTICS_EVENTS.ACCOUNT_CREATION_SUCCESS_MODAL_VIEWED);
    }
  }, [showSuccessModal, track]);

  const goToOnboard = () => window.location.replace('/onboard');

  const handlePasteCode = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (otpInputRef.current) {
        otpInputRef.current.fill(text);
      }
    } catch {
      // clipboard access denied — user can paste manually
    }
  };

  return (
    <div className="auth__content">
      <h1 className="page__title">
        Verify your <span className="text-cursive">email</span>
      </h1>
      <div className="auth__card">
        <Logo className="logo" />
        <div className="register-v2__otp">
          <div className="register-v2__otp-meta">
            <p>
              We sent a 6 digit code to <strong>{maskedEmail || email}.</strong>
            </p>
            <p>Enter the code below.</p>
          </div>

          <div className="register-v2__otp-group">
            <OTPInput ref={otpInputRef} length={6} onChange={handleOtpChange} error={otpError} />

            <div className="register-v2__otp-actions">
              <button
                type="button"
                className="register-v2__otp-retry"
                onClick={handleResend}
                disabled={resendCountdown > 0 || isResending}>
                {resendCountdown > 0 ? `Retry in ${resendCountdown}s` : 'Retry'}
              </button>
              <button type="button" className="register-v2__otp-paste" onClick={handlePasteCode}>
                Paste code
              </button>
            </div>

            <Button
              isFull
              isLoading={isVerifying}
              disabled={otpValue.length !== 6}
              onClick={handleVerify}>
              Verify email
            </Button>
          </div>

          <p className="auth__link">
            I already have an account, <Link to="/login">Log in</Link>
          </p>

          {showSuccessModal && (
            <Modal
              Icon={SuccessIcon}
              header={{ name: 'Account created successfully' }}
              onModalClose={goToOnboard}>
              <p className="modal__text">
                To get started with basqet, we need a few details to verify your identity and set up
                your account.
              </p>
              <Button isFull onClick={goToOnboard}>
                Get Started
              </Button>
            </Modal>
          )}
        </div>
      </div>
    </div>
  );
}

OTPVerification.propTypes = {
  email: PropTypes.string.isRequired,
  maskedEmail: PropTypes.string,
  onMaskedEmailChange: PropTypes.func.isRequired,
  onRestart: PropTypes.func.isRequired,
  autoSend: PropTypes.bool
};

export { RegisterV2 };
